/**
 * @file data-helper.tsx
 * @description Утилиты для работы с данными в Task Manager приложении. Содержит функции для обработки временных ID, генерации GUID, объединения массивов с проверкой уникальности, сортировки по типу и сериализации/десериализации JSON объектов для унифицированной работы с данными в таблицах, формах и API запросах.
 * @author AI-Generated | Human-Reviewed
 * @created 2025-01-15
 * @updated 2025-09-22
 * @dependencies []
 * @related-files ['src/components/ui/tables', 'src/api', 'src/forms', 'src/utils/fake-helper.ts']
 */

import { IBaseObjectDTO, ITableItemDTO } from '@src/types/dto';

/**
 * Префикс для временных ID генерации
 */
const TEMP_ID_PREFIX = 'u';

/**
 * Удаляет временные ID генерации из данных таблицы
 * @param tableData - массив объектов данных таблицы
 * @returns новый массив с очищенными временными ID (начинающиеся с 'u')
 */
export const removeTableGenerationIds = (tableData: any[]) => {
    return tableData.map((item: any) => (item.id && item.id[0] === TEMP_ID_PREFIX ? { ...item, id: undefined } : item));
};

/**
 * Генерирует фейковый GUID в формате xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
 * @returns строка с случайно сгенерированным GUID
 */
export const generateFakeGuid = (): string => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.floor(Math.random() * 16);
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
};

/**
 * Объединяет два массива, удаляя дублирующиеся значения по указанному полю
 * @param existing - существующий массив значений
 * @param incoming - новый массив значений для объединения
 * @param uniqueFieldName - имя поля для проверки уникальности (по умолчанию 'id')
 * @returns новый массив с уникальными значениями
 */
export const mergeUniqueValues = (existing: any[], incoming: any[], uniqueFieldName: string = 'id'): any[] => {
    const combined = [...(existing || []), ...(incoming || [])];
    const uniqueMap = new Map();
    combined.forEach((item) => {
        uniqueMap.set(item[uniqueFieldName], item);
    });

    return Array.from(uniqueMap.values());
};

/**
 * Сортирует массив по возрастанию числового значения поля item.type.id
 */
export function sortByTypeId<T extends { type: { id: string } }>(items: T[]): T[] {
    return [...items].sort((a, b) => {
        const idA = Number(a.type.id);
        const idB = Number(b.type.id);
        return idA - idB;
    });
}

/**
 * Преобразует любой JSON-совместимый объект в строку.
 * @param obj — объект для сериализации
 * @returns строка в формате JSON
 */
export function objectToString(obj: IBaseObjectDTO): string {
    return JSON.stringify(obj);
}

/**
 * Преобразует строку (JSON) обратно в объект.
 * @param str — строка, полученная из objectToString
 * @returns восстановленный объект или выбрасывает ошибку, если строка некорректна
 */
export function stringToObject(str: string): IBaseObjectDTO {
    return JSON.parse(str) as IBaseObjectDTO;
}

import {
    TAttachment,
    IUploadedFile,
    INewFile,
    INewLink,
    INewSystemObject,
    TNewAttachment
} from '@src/types/attachment';
import { GuidUtils } from '@src/types/guid';
import {
    ATTACHMENT_TYPE_IDS,
    ATTACHMENT_TYPE_NAMES,
    ATTACHMENT_TYPE_DISPLAY_NAMES
} from '@src/constants/attachment-types';

/**
 * Преобразует загруженный файл в структуру для добавления в attachments
 * @param uploadedFile - файл после загрузки на сервер
 * @param fileTypeMetadata - дополнительные метаданные типа файла (опционально)
 * @returns новый файл готовый для добавления в attachments
 */
export const createNewFileFromUploaded = (
    uploadedFile: IUploadedFile,
    fileTypeMetadata?: { id: string; name: string }
): INewFile => {
    return {
        ...uploadedFile,
        type: {
            id: fileTypeMetadata?.id || ATTACHMENT_TYPE_IDS.FILE,
            $type: ATTACHMENT_TYPE_NAMES.ATTACHMENT_TYPE_ENUM,
            name: fileTypeMetadata?.name || ATTACHMENT_TYPE_DISPLAY_NAMES[ATTACHMENT_TYPE_IDS.FILE]
        }
    };
};

/**
 * Создает новую ссылку для добавления в attachments
 * @param url - URL ссылки
 * @param displayName - отображаемое имя (опционально, по умолчанию = url)
 * @returns новая ссылка готовая для добавления в attachments
 */
export const createNewLink = (url: string, displayName?: string): INewLink => {
    return {
        link: url,
        name: displayName || url,
        type: ATTACHMENT_TYPE_NAMES.LINK as 'link'
    };
};

/**
 * Создает новый объект системы для добавления в attachments
 * @param systemObject - объект системы (IBaseObjectDTO)
 * @param displayName - отображаемое имя (опционально, по умолчанию = object.name)
 * @returns новый объект системы готовый для добавления в attachments
 */
export const createNewSystemObject = (systemObject: IBaseObjectDTO, displayName?: string): INewSystemObject => {
    return {
        link: JSON.stringify(systemObject),
        name: displayName || systemObject.name,
        type: ATTACHMENT_TYPE_NAMES.OBJECT as 'object'
    };
};

/**
 * Преобразует новое вложение в полную структуру TAttachment для API
 * @param newAttachment - новое вложение любого типа
 * @param metadata - дополнительные метаданные (id, author и т.д.)
 * @returns полная структура TAttachment для отправки на сервер
 */
export const convertNewAttachmentToFull = (
    newAttachment: TNewAttachment,
    metadata?: {
        id?: string;
        createdBy?: string;
        modifiedAt?: number;
    }
): TAttachment => {
    const baseAttachment: TAttachment = {
        id: metadata?.id || GuidUtils.empty(),
        $type: ATTACHMENT_TYPE_NAMES.ATTACHMENT,
        name: newAttachment.name,
        link: newAttachment.link as string, // Приведение типа для совместимости
        type: convertAttachmentType(newAttachment.type),
        createdBy: metadata?.createdBy,
        modifiedAt: metadata?.modifiedAt || Date.now()
    };

    // Добавляем размер для файлов
    if ('size' in newAttachment && newAttachment.size) {
        baseAttachment.size = newAttachment.size;
    }

    return baseAttachment;
};

/**
 * Преобразует тип вложения в IBaseObjectDTO
 * @param type - тип из TNewAttachment
 * @returns стандартизированный тип для TAttachment
 */
const convertAttachmentType = (type: any): IBaseObjectDTO => {
    if (typeof type === 'string') {
        switch (type) {
            case ATTACHMENT_TYPE_NAMES.LINK:
                return {
                    id: ATTACHMENT_TYPE_IDS.LINK,
                    $type: ATTACHMENT_TYPE_NAMES.ATTACHMENT_TYPE_ENUM,
                    name: ATTACHMENT_TYPE_DISPLAY_NAMES[ATTACHMENT_TYPE_IDS.LINK]
                };
            case ATTACHMENT_TYPE_NAMES.OBJECT:
                return {
                    id: ATTACHMENT_TYPE_IDS.SYSTEM_OBJECT,
                    $type: ATTACHMENT_TYPE_NAMES.ATTACHMENT_TYPE_ENUM,
                    name: ATTACHMENT_TYPE_DISPLAY_NAMES[ATTACHMENT_TYPE_IDS.SYSTEM_OBJECT]
                };
        }
    }

    if (typeof type === 'object' && type.$type) {
        return type as IBaseObjectDTO;
    }

    // По умолчанию - файл
    return {
        id: ATTACHMENT_TYPE_IDS.FILE,
        $type: ATTACHMENT_TYPE_NAMES.ATTACHMENT_TYPE_ENUM,
        name: ATTACHMENT_TYPE_DISPLAY_NAMES[ATTACHMENT_TYPE_IDS.FILE]
    };
};

/**
 * Проверяет является ли вложение файлом
 * @param attachment - вложение для проверки
 * @returns true если это файл
 */
export const isFileAttachment = (attachment: TAttachment): boolean => {
    return attachment.type?.id === ATTACHMENT_TYPE_IDS.FILE;
};

/**
 * Проверяет является ли вложение ссылкой
 * @param attachment - вложение для проверки
 * @returns true если это ссылка
 */
export const isLinkAttachment = (attachment: TAttachment): boolean => {
    return attachment.type?.id === ATTACHMENT_TYPE_IDS.LINK;
};

/**
 * Проверяет является ли вложение объектом системы
 * @param attachment - вложение для проверки
 * @returns true если это объект системы
 */
export const isSystemObjectAttachment = (attachment: TAttachment): boolean => {
    return attachment.type?.id === ATTACHMENT_TYPE_IDS.SYSTEM_OBJECT;
};

/**
 * Парсит JSON строку объекта системы из вложения
 * @param attachment - вложение типа "объект системы"
 * @returns распарсенный объект системы или null если парсинг не удался
 */
export const parseSystemObjectFromAttachment = (attachment: TAttachment): IBaseObjectDTO | null => {
    if (!isSystemObjectAttachment(attachment)) {
        return null;
    }

    try {
        const parsed = JSON.parse(attachment.link);
        // Проверяем что это валидный IBaseObjectDTO
        if (parsed && parsed.$type && parsed.id && parsed.name) {
            return parsed as IBaseObjectDTO;
        }
        return null;
    } catch (error) {
        console.error('Ошибка парсинга объекта системы:', error);
        return null;
    }
};

/**
 * Обновляет строку в массиве данных таблицы с умной обработкой различных типов полей
 * @param rows - массив строк таблицы
 * @param updatedItem - обновленный элемент
 * @returns новый массив с обновленной строкой
 */
export const updateTableRow = (rows: ITableItemDTO[], updatedItem: Partial<Record<string, any>> & { id: string }): ITableItemDTO[] => {
    if (!rows || !updatedItem?.id) {
        return rows;
    }

    return rows.map((row) => {
        if (row.id !== updatedItem.id) {
            return row;
        }

        // Используем типобезопасный подход через object spread
        const rowAsRecord = row as Record<string, any>;
        const updatedAsRecord = updatedItem as Record<string, any>;
        
        return Object.keys(rowAsRecord).reduce((acc, key) => {
            // Если поле отсутствует в обновлении, оставляем старое значение
            if (updatedAsRecord[key] === undefined) {
                (acc as any)[key] = rowAsRecord[key];
                return acc;
            }

            const oldValue = rowAsRecord[key];
            const newValue = updatedAsRecord[key];

            // Обработка объектных полей
            if (typeof oldValue === 'object' && oldValue !== null) {
                if (typeof newValue === 'object' && newValue !== null) {
                    // Оба значения объекты - используем новое
                    (acc as any)[key] = newValue;
                } else {
                    // Новое значение примитив - обновляем поле name в объекте
                    (acc as any)[key] = { ...oldValue, name: newValue };
                }
            } else {
                // Старое значение примитив
                if (typeof newValue === 'object' && newValue !== null) {
                    // Новое значение объект - берем его name
                    (acc as any)[key] = (newValue as any).name || newValue;
                } else {
                    // Оба значения примитивы - используем новое
                    (acc as any)[key] = newValue;
                }
            }

            return acc;
        }, {} as ITableItemDTO);
    });
};
